import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  getAddProjectPlanetTileTemplate,
  getAddProjectSatelliteTileTemplate,
  getDefaultMapTileTemplate,
  getIndexTile,
  getPlanetTileTemplate,
  getSatelliteTileTemplate,
  getTileBanStatus,
} from "../api/FlaskAPI";
import {
  clearTileBan,
  setAddProjectPlanetTileTemplate,
  setAddProjectSatelliteTileTemplate,
  setDefaultMapTileTemplate,
  setPlanetTileTemplate,
  setSatelliteTileTemplate,
  setTileBan,
  updateTileUrl,
} from "../reducers/MapSlice";

// Re-mint signed tile URL templates this many seconds before they expire.
export const TILE_REFRESH_MARGIN_SECONDS = 600; // 10 minutes
// check all tiles for refresh
const SWEEP_MILLSECONDS = 480000; // 8 minutes
export const nowSeconds = () => Math.floor(Date.now() / 1000);

/**
 * Keeps the signed raster tile URL templates (index imagery + satellite
 * basemap) valid for the whole session:
 *  - proactively re-mints any template approaching its expiry (periodic sweep
 *    + on tab focus, since timers are throttled in background tabs);
 *  - reactively re-mints a layer's template when one of its tiles fails to
 *    load (e.g. the token expired while the tab was asleep).
 */
export function useTileTemplateRefresh({ addProjectToken } = {}) {
  const dispatch = useDispatch();
  const store = useStore();
  const tileLayer = useSelector((state) => state.mapState.tileLayer);
  const currentWorkspace = useSelector(
    (state) => state.mapState.currentWorkspace,
  );
  const drawingMode = useSelector((state) => state.mapState.drawingMode);
  const boundaryMethod = useSelector((state) => state.mapState.boundaryMethod);
  // Guards against a stampede of re-mint requests when many tiles of the same
  // layer fail at once.
  const refreshingRef = useRef(new Set());
  const banCheckInProgressRef = useRef(false);

  const handleForbidden = useCallback(
    (error) => {
      const status = error.response?.status;
      const data = error.response?.data || {};
      if (status === 403) {
        dispatch(
          setTileBan({
            message:
              data.message ||
              "You have been temporarily blocked from loading map tiles due to suspicious activity.",
            remainingSeconds: data.remaining_seconds ?? null,
          }),
        );
        return true;
      }
      return false;
    },
    [dispatch],
  );

  const checkBanStatus = useCallback(async () => {
    if (banCheckInProgressRef.current) return;
    banCheckInProgressRef.current = true;
    try {
      const res = await getTileBanStatus();
      if (res.data.banned) {
        dispatch(
          setTileBan({
            message: res.data.message,
            remainingSeconds: res.data.remaining_seconds ?? null,
          }),
        );
      } else {
        dispatch(clearTileBan());
      }
    } catch (error) {
      handleForbidden(error);
    } finally {
      banCheckInProgressRef.current = false;
    }
  }, [dispatch, handleForbidden]);

  const refreshTemplate = useCallback(
    async (stateKey, refreshKey, fetchFn, setAction, errorMessage) => {
      const state = store.getState().mapState;
      const isFresh =
        state[stateKey].tileUrlExpiresAt - TILE_REFRESH_MARGIN_SECONDS >
        nowSeconds();
      if (isFresh || refreshingRef.current.has(refreshKey)) return;
      refreshingRef.current.add(refreshKey);
      try {
        const res = await fetchFn();
        dispatch(
          setAction({
            tileUrlTemplate: res.data.tile_url_template,
            attribution: res.data.attribution,
            expiresAt: res.data.expires_at,
          }),
        );
      } catch (error) {
        if (!handleForbidden(error)) {
          console.error(errorMessage, error);
        }
      } finally {
        refreshingRef.current.delete(refreshKey);
      }
    },
    [dispatch, store, handleForbidden],
  );

  const refreshSatelliteTemplate = useCallback(
    () =>
      refreshTemplate(
        "satelliteLayer",
        "satellite",
        getSatelliteTileTemplate,
        setSatelliteTileTemplate,
        "Error refreshing satellite tile template:",
      ),
    [refreshTemplate],
  );

  const refreshPlanetTemplate = useCallback(
    () =>
      refreshTemplate(
        "planetLayer",
        "planet",
        getPlanetTileTemplate,
        setPlanetTileTemplate,
        "Error refreshing planet tile template:",
      ),
    [refreshTemplate],
  );

  const refreshDefaultMapTemplate = useCallback(
    () =>
      refreshTemplate(
        "defaultmapLayer",
        "defaultmap",
        getDefaultMapTileTemplate,
        setDefaultMapTileTemplate,
        "Error refreshing default map tile template:",
      ),
    [refreshTemplate],
  );

  const refreshAddProjectSatelliteTemplate = useCallback(
    () =>
      refreshTemplate(
        "addProjectSatelliteLayer",
        "addProjectSatellite",
        () => getAddProjectSatelliteTileTemplate(addProjectToken),
        setAddProjectSatelliteTileTemplate,
        "Error refreshing add-project satellite tile template:",
      ),
    [refreshTemplate, addProjectToken],
  );

  const refreshAddProjectPlanetTemplate = useCallback(
    () =>
      refreshTemplate(
        "addProjectPlanetLayer",
        "addProjectPlanet",
        () => getAddProjectPlanetTileTemplate(addProjectToken),
        setAddProjectPlanetTileTemplate,
        "Error refreshing add-project planet tile template:",
      ),
    [refreshTemplate, addProjectToken],
  );

  const refreshIndexTile = useCallback(
    async (tile) => {
      const isFresh =
        tile.tileUrlExpiresAt - TILE_REFRESH_MARGIN_SECONDS > nowSeconds();
      if (isFresh || refreshingRef.current.has(tile.tileId)) return;
      refreshingRef.current.add(tile.tileId);
      try {
        const res = await getIndexTile(tile.tileParams);
        dispatch(
          updateTileUrl({
            tileId: tile.tileId,
            tileUrl: res.data.tile_url_template,
            tileUrlExpiresAt: res.data.expires_at,
          }),
        );
      } catch (error) {
        if (!handleForbidden(error)) {
          console.error("Error refreshing index tile template:", error);
        }
      } finally {
        refreshingRef.current.delete(tile.tileId);
      }
    },
    [dispatch, handleForbidden],
  );

  useEffect(() => {
    const sweep = () => {
      const state = store.getState().mapState;
      const drawingActive =
        !!state.drawingMode && state.boundaryMethod === "draw";
      const planetEnabled = state.satelliteTilesAvailable.planetTiles;
      if (tileLayer === "satellite") {
        if (drawingActive && planetEnabled) {
          if (addProjectToken) {
            refreshAddProjectPlanetTemplate();
          } else {
            refreshPlanetTemplate();
          }
        } else if (addProjectToken) {
          refreshAddProjectSatelliteTemplate();
        } else {
          refreshSatelliteTemplate();
        }
      } else if (tileLayer === "defaultmap") {
        refreshDefaultMapTemplate();
      }
      (state.workspaces[currentWorkspace] || []).forEach((tile) =>
        refreshIndexTile(tile),
      );
    };

    sweep();
    const intervalId = setInterval(sweep, SWEEP_MILLSECONDS);
    const onVisibilityChange = () => {
      if (!document.hidden) sweep();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    store,
    tileLayer,
    currentWorkspace,
    drawingMode,
    boundaryMethod,
    addProjectToken,
    refreshSatelliteTemplate,
    refreshPlanetTemplate,
    refreshDefaultMapTemplate,
    refreshAddProjectSatelliteTemplate,
    refreshAddProjectPlanetTemplate,
    refreshIndexTile,
  ]);

  // tileerror carries no HTTP status, so only re-mint when the template is
  // actually due for renewal; persistent non-auth failures (e.g. outside the
  // project area) cannot cause a refresh loop.
  const handleIndexTileError = useCallback(
    (tileId) => {
      const state = store.getState().mapState;
      if (state.tileBan.banned) return;

      const tile = (state.workspaces[state.currentWorkspace] || []).find(
        (t) => t.tileId === tileId,
      );
      if (tile) {
        refreshIndexTile(tile).then(() => checkBanStatus());
      } else {
        checkBanStatus();
      }
    },
    [store, refreshIndexTile, checkBanStatus],
  );

  const handleSatelliteTileError = useCallback(() => {
    const state = store.getState().mapState;
    if (state.tileBan.banned) return;

    if (addProjectToken) {
      refreshAddProjectSatelliteTemplate().then(() => checkBanStatus());
    } else {
      refreshSatelliteTemplate().then(() => checkBanStatus());
    }
  }, [
    store,
    addProjectToken,
    refreshSatelliteTemplate,
    refreshAddProjectSatelliteTemplate,
    checkBanStatus,
  ]);

  const handlePlanetTileError = useCallback(() => {
    const state = store.getState().mapState;
    if (state.tileBan.banned) return;

    if (addProjectToken) {
      refreshAddProjectPlanetTemplate().then(() => checkBanStatus());
    } else {
      refreshPlanetTemplate().then(() => checkBanStatus());
    }
  }, [
    store,
    addProjectToken,
    refreshPlanetTemplate,
    refreshAddProjectPlanetTemplate,
    checkBanStatus,
  ]);

  const handleDefaultMapTileError = useCallback(() => {
    const state = store.getState().mapState;
    if (state.tileBan.banned) return;

    refreshDefaultMapTemplate().then(() => checkBanStatus());
  }, [store, refreshDefaultMapTemplate, checkBanStatus]);

  return {
    handleIndexTileError,
    handleSatelliteTileError,
    handlePlanetTileError,
    handleDefaultMapTileError,
    checkBanStatus,
  };
}
